import React from 'react';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { useCookies } from 'next-client-cookies';
import ReactMarkdown from 'react-markdown';
import { Log } from 'helpers/errorLogger';

export interface Props {
  markdown: string;
  className?: string;
  style?: React.CSSProperties;
  openInNewTab?: boolean;
  locale?: string;
}
interface MarkdownLinkProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  openInNewTab: boolean;
  locale: string;
  addLocaleQueryParam: (href?: string) => string | undefined;
}

const MarkdownLink: React.FC<MarkdownLinkProps> = ({
  href = '',
  openInNewTab,
  locale,
  addLocaleQueryParam,
  children,
  ...restProps
}) => {
  const finalPath = locale && !href.startsWith(`/${locale}`) ? `/${locale}${href}` : href;

  const hrefWithLocaleContext = openInNewTab ? addLocaleQueryParam(finalPath) : finalPath;

  return (
    <a
      {...restProps}
      href={hrefWithLocaleContext}
      target={openInNewTab ? '_blank' : undefined}
      rel={openInNewTab ? 'noopener noreferrer' : undefined}
    >
      {children}
      {openInNewTab && <ArrowTopRightOnSquareIcon className="inline ml-2 pb-4 size-18" aria-hidden="true" />}
    </a>
  );
};

const MarkdownRM: React.FC<Props> = ({
  markdown,
  className = '',
  style = {},
  openInNewTab = false,
  locale: localeProp,
}) => {
  const cookies = useCookies();
  const locale = (cookies.get('locale') ?? localeProp) as string;

  if (typeof markdown !== 'string') {
    Log.error(new Error(`Markdown: Invalid markdown property. Expected string but received ${typeof markdown}`));
    return <></>;
  }

  const markdownStyles: React.CSSProperties = {
    lineHeight: '1.6',
    ...style,
  };

  const addLocaleQueryParam = (href?: string) => {
    if (!href) return href;
    if (!locale) return href;
    if (!href.startsWith('/')) return href;

    const [pathAndQuery, hash = ''] = href.split('#');
    const [pathname, query = ''] = pathAndQuery.split('?');
    const searchParams = new URLSearchParams(query);

    if (!searchParams.has('hl')) {
      searchParams.set('hl', locale);
    }

    const nextQuery = searchParams.toString();
    const nextPathAndQuery = nextQuery ? `${pathname}?${nextQuery}` : pathname;
    const nextHash = hash ? `#${hash}` : '';

    return `${nextPathAndQuery}${nextHash}`;
  };

  const components: any = {
    a: (linkProps: any) => (
      <MarkdownLink
        {...linkProps}
        locale={locale}
        openInNewTab={openInNewTab}
        addLocaleQueryParam={addLocaleQueryParam}
      />
    ),
  };

  return (
    <div className={`prose prose-sm max-w-none ${className}`} style={markdownStyles}>
      <ReactMarkdown components={components}>{markdown}</ReactMarkdown>
    </div>
  );
};

export default MarkdownRM;
